// ============================================================================
// Characters redesign: frozen API contract + client-side mock persistence.
//
// The backend does not yet know these fields. Until it does, this module IS
// the contract: field names below are what the server will return on every
// character endpoint (list / presentations / detail) and accept on the
// standard character update. Persistence is localStorage, per project.
//
// Frozen contract (server-side, snake_case, flat on the character):
//   importance:        'main' | 'secondary' | 'episodic' | 'extra' | null
//   categories:        SignCharacter[]   — ordered character arc, max 2;
//                      scalar `category` is KEPT and mirrors categories[0]
//   characteristic:    string | null     — card-level one-liner, distinct
//                      from the Pro characteristics[] list
//   social_profession / social_income / social_education / social_family:
//                      string | null     — «Социальное положение» section
//   personality / problems / motivation:
//                      string | null     — «Психологический профиль» section
//                      (Предыстория = existing `description`, Цели = existing
//                      `initial_goal`)
//   Dreamcast:         assetCategory.dreamcast — ordinary character assets
//                      uploaded with category='dreamcast'. Until then uploads
//                      stay category='image' and membership lives in the
//                      mock-only `dreamcast_asset_ids`.
//   Reference / Costumes (2026-08-27 design update): the «Референс» and
//                      «Костюмы и грим» tabs are commentable media galleries.
//                      Same shape as dreamcast — assetCategory.reference /
//                      assetCategory.costume server-side eventually, mock-only
//                      `reference_asset_ids` / `costume_asset_ids` until then.
//   asset_comments:    Record<asset_id, MediaComment[]> — the media comment
//                      threads of the gallery tabs. Server-side these become
//                      ordinary polymorphic comments on the asset; until then
//                      the whole per-asset thread is the unit of persistence.
//   Video links (2026-09-03): GET /oembed/resolve is LIVE on test8 — the
//                      resolution is real, only the STORAGE is mocked:
//                      `video_links` entries { id: NEGATIVE client-minted
//                      number, collection, source_url, meta } render as
//                      pseudo-assets in the galleries and their comment
//                      threads key off the negative id in asset_comments.
//                      Swap path: when the assets table grows source_url/meta
//                      columns, links become ordinary assets[] rows (category
//                      = collection, link-ness = source_url IS NOT NULL) and
//                      this field dies. thumbnail_url stays REMOTE by design;
//                      duration is Vimeo-only.
//   Actors (2026-09-03 partial landing, NOT yet deployed): the backend grew a
//                      GLOBAL actors dictionary (POST/PUT/DELETE /api/actors:
//                      name+surname+age string+gender only) and per-PROJECT
//                      attachments (POST/DELETE projects/{p}/actors: pivot
//                      group 'dreamcast'|'casting' + approved bool). The
//                      casting tab stays extras-read-first because the server
//                      knows NO per-character linkage, no photo/birth_date/
//                      physique/skills/filmography, and «Утверждённые» is the
//                      approved FLAG, not a third group. useActorMutations
//                      dual-writes: global record + project attachment
//                      best-effort (silent while the routes 404), server id
//                      remembered as CharacterActor.actor_id.
//   Merge:             POST projects/{p}/characters/{target_id}/merge
//                      body { source_id } → merged ICharacter.
//
// Swap path when the backend lands:
//   1. useUpdateCharacterExtras posts to the real endpoint; storage code dies.
//   2. enrichCharacter calls disappear from the entity fetchers.
//   3. EXTRA_FIELD_NAMES shrinks to empty — splitCharacterPatch routes
//      everything to the real update.
//   4. Dreamcast membership reads asset.category === 'dreamcast'.
// No component changes anywhere.
//
// Known limitation (as with the Notes freeze, #1826/#1827): extras are
// per-browser and ignore snapshot versions until the backend lands.
//
// PARTIAL LANDING (backend feature/extended_characters, live on test8
// 2026-08-25, commits 5b1c18eb + 52f469c7): the characters table gained real
// columns — categories, age, occupation, profession, household, education,
// family_status, backstory, personality, goals, conflicts, motivation. The
// detail endpoint now returns them all. Live-probed quirks that shape the
// adapter below:
//   * `categories` entries validate against dictionary KEYS ('protagonist',
//     incl. ally_antagonist and the new neutral) — rows written before the
//     52f469c7 fix still store LABELS ('Protagonist'), so reads accept both;
//   * `categories` is REQUIRED NON-EMPTY on BOTH create and update — every
//     real character update must bundle it (resolveServerCategories) or the
//     whole request 422s;
//   * empty strings still 422 on every new field (TrimStrings +
//     ConvertEmptyStringsToNull, validation has no nullable) — a field can
//     never be cleared server-side, so extras stay the read-first source and
//     '' writes stay mock-only.
// Real writes mirror non-empty values into the new columns (contract name →
// server name), and the legacy homes (description/initial_goal/role/
// age_category) keep working exactly as before with the landed column
// (backstory/goals/occupation/age) dual-written alongside.
// ============================================================================

import { queryClient } from '@/config/queryClient';
import { assetCategory, type CharacterImportance, SignCharacter } from '@/enums';
import type { Assets, CharacterActor, ICharacter, MediaComment, VideoLinkEntry } from '@/types';

export interface CharacterExtras {
    importance: CharacterImportance | null;
    categories: SignCharacter[];
    characteristic: string | null;
    social_profession: string | null;
    social_income: string | null;
    social_education: string | null;
    social_family: string | null;
    personality: string | null;
    problems: string | null;
    motivation: string | null;
    /** Mock-only: dreamcast membership until assetCategory.dreamcast is real server-side. */
    dreamcast_asset_ids: number[];
    /** Mock-only: «Референс» gallery membership until assetCategory.reference is real server-side. */
    reference_asset_ids: number[];
    /** Mock-only: «Костюмы и грим» gallery membership until assetCategory.costume is real server-side. */
    costume_asset_ids: number[];
    /** Media comment threads per asset id; each asset's whole thread is the unit of persistence. */
    asset_comments: Record<number, MediaComment[]>;
    /** Mock-only pasted video links (resolution is real, storage is not); the whole list is the unit of persistence. */
    video_links: VideoLinkEntry[];
    /** Casting actors; the whole list is the unit of persistence. */
    actors: CharacterActor[];
}

/**
 * Fields the splitter routes to the mock store instead of the real update.
 * Shrinks to empty at swap time — then every patch goes to the server.
 */
export const EXTRA_FIELD_NAMES: ReadonlySet<string> = new Set([
    'importance',
    'categories',
    'characteristic',
    'social_profession',
    'social_income',
    'social_education',
    'social_family',
    'personality',
    'problems',
    'motivation',
    'dreamcast_asset_ids',
    'reference_asset_ids',
    'costume_asset_ids',
    'asset_comments',
    'video_links',
    'actors',
] satisfies Array<keyof CharacterExtras>);

// Contract fields whose server column landed, keyed by contract name. Values
// are the wire names the real update expects / list responses carry.
export const SERVER_FIELD_BY_CONTRACT: Readonly<Record<string, string>> = {
    social_profession: 'profession',
    social_income: 'household',
    social_education: 'education',
    social_family: 'family_status',
    personality: 'personality',
    problems: 'conflicts',
    motivation: 'motivation',
};

// Legacy columns that remain the editable home; each write also mirrors into
// the landed column so server-side consumers (export, Filmtoolz) see the data.
const MIRROR_BY_LEGACY: Readonly<Record<string, string>> = {
    description: 'backstory',
    initial_goal: 'goals',
    role: 'occupation',
    age_category: 'age',
};

// The server now validates `categories` against the dictionary KEYS — the
// same strings as the SignCharacter values. Rows written before the 52f469c7
// backend fix still hold LABELS ('Protagonist'), so reads accept both forms.
const CATEGORY_LABEL_BY_SIGN: Readonly<Record<SignCharacter, string>> = {
    protagonist: 'Protagonist',
    antagonist: 'Antagonist',
    ally: 'Ally',
    secondary: 'Secondary',
    hidden_antagonist: 'Hidden Antagonist',
    ally_antagonist: 'Ally Antagonist',
    neutral: 'Neutral',
};

const SIGN_BY_SERVER_VALUE: Readonly<Record<string, SignCharacter>> = Object.fromEntries([
    ...Object.entries(CATEGORY_LABEL_BY_SIGN).map(([sign, label]) => [label as string, sign as SignCharacter]),
    ...Object.keys(CATEGORY_LABEL_BY_SIGN).map(sign => [sign, sign as SignCharacter]),
]);

/** Arc → server wire form (dictionary keys); unknown junk is dropped. */
export function toServerCategories(categories: readonly SignCharacter[]): string[] {
    return categories.filter(sign => sign in CATEGORY_LABEL_BY_SIGN);
}

/** Server wire values (keys, or labels from pre-fix rows) → arc. */
export function fromServerCategories(values: unknown): SignCharacter[] {
    if (!Array.isArray(values)) return [];
    return values.map(value => SIGN_BY_SERVER_VALUE[String(value)]).filter((sign): sign is SignCharacter => sign !== undefined);
}

/** CREATE requires a non-empty `categories`; new characters start as secondary. */
export const DEFAULT_CREATE_CATEGORIES: readonly string[] = ['secondary'];

/** The new columns 422 on empty strings (no nullable server-side). */
const isRealWritable = (value: unknown): value is string => typeof value === 'string' && value.trim() !== '';

/**
 * The server REQUIRES a non-empty `categories` on every character update —
 * a single-field autosave patch would 422 without it. Resolves the value the
 * UI currently treats as truth: the extras record first (mirrors
 * enrichCharacter's read order), then any cached server copy, then the
 * scalar role; secondary as the last resort so the bundle is never empty.
 */
export function resolveServerCategories(projectId: number, characterId: number): string[] {
    const extras = readExtras(projectId, characterId);
    const fromExtras = toServerCategories(extras.categories ?? []);
    if (fromExtras.length > 0) return fromExtras;

    let cached: ICharacter | undefined;
    for (const [, data] of queryClient.getQueriesData<unknown>({ queryKey: ['characters'] })) {
        if (Array.isArray(data)) {
            cached = (data as ICharacter[]).find(item => item?.id === characterId);
        } else if ((data as ICharacter | undefined)?.id === characterId) {
            cached = data as ICharacter;
        }
        if (cached) break;
    }
    const cachedArc = toServerCategories(fromServerCategories(cached?.categories));
    if (cachedArc.length > 0) return cachedArc;
    const scalar = toServerCategories(cached?.category ? [cached.category] : []);
    if (scalar.length > 0) return scalar;

    return [SignCharacter.secondary];
}

type ExtrasByCharacter = Record<string, Partial<CharacterExtras>>;

const storageKey = (projectId: number) => `characterExtras:${projectId}`;

function readAll(projectId: number): ExtrasByCharacter {
    try {
        const raw = localStorage.getItem(storageKey(projectId));
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
        return parsed as ExtrasByCharacter;
    } catch {
        return {};
    }
}

function writeAll(projectId: number, all: ExtrasByCharacter) {
    localStorage.setItem(storageKey(projectId), JSON.stringify(all));
}

export function readExtras(projectId: number, characterId: number): Partial<CharacterExtras> {
    return readAll(projectId)[characterId] ?? {};
}

/** Merges a patch into the character's extras record. Optimistic entries (negative ids) are never persisted. */
export function writeExtras(projectId: number, characterId: number, patch: Partial<CharacterExtras>) {
    if (characterId <= 0) return;
    const all = readAll(projectId);
    all[characterId] = { ...all[characterId], ...patch };
    writeAll(projectId, all);
}

// Extras keys that merge target-empty-wins when one character absorbs another.
const ABSORBABLE_TEXT_KEYS = [
    'importance',
    'characteristic',
    'social_profession',
    'social_income',
    'social_education',
    'social_family',
    'personality',
    'problems',
    'motivation',
] as const;

/**
 * Merge-time extras transfer: the target keeps its own values, the source
 * fills only the target's empty slots; dreamcast ids are unioned. The source
 * record is removed so a future character reusing the id inherits nothing.
 */
export function absorbExtras(projectId: number, targetId: number, sourceId: number) {
    if (targetId <= 0 || sourceId <= 0) return;
    const source = readExtras(projectId, sourceId) as Record<string, unknown>;
    const target = readExtras(projectId, targetId) as Record<string, unknown>;
    const merged: Record<string, unknown> = { ...target };
    for (const key of ABSORBABLE_TEXT_KEYS) {
        if (!merged[key] && source[key]) merged[key] = source[key];
    }
    const targetCategories = target.categories as SignCharacter[] | undefined;
    const sourceCategories = source.categories as SignCharacter[] | undefined;
    if (!targetCategories?.length && sourceCategories?.length) merged.categories = sourceCategories;
    for (const key of ['dreamcast_asset_ids', 'reference_asset_ids', 'costume_asset_ids'] as const) {
        const union = [...new Set([...((target[key] as number[]) ?? []), ...((source[key] as number[]) ?? [])])];
        if (union.length) merged[key] = union;
    }
    // Asset ids are globally unique, so the two comment maps never collide.
    const comments = {
        ...((source.asset_comments as Record<number, MediaComment[]>) ?? {}),
        ...((target.asset_comments as Record<number, MediaComment[]>) ?? {}),
    };
    if (Object.keys(comments).length) merged.asset_comments = comments;
    const actors = [...((target.actors as CharacterActor[]) ?? []), ...((source.actors as CharacterActor[]) ?? [])];
    if (actors.length) merged.actors = actors;
    const videoLinks = [...((target.video_links as VideoLinkEntry[]) ?? []), ...((source.video_links as VideoLinkEntry[]) ?? [])];
    if (videoLinks.length) merged.video_links = videoLinks;
    writeExtras(projectId, targetId, merged as Partial<CharacterExtras>);
    removeExtras(projectId, sourceId);
}

/** Deletes the character's extras record (character deleted or merged away). */
export function removeExtras(projectId: number, characterId: number) {
    const all = readAll(projectId);
    if (!(characterId in all)) return;
    delete all[characterId];
    writeAll(projectId, all);
}

/**
 * Grafts the frozen-contract fields onto a server character. Runs inside the
 * entity fetchers, so data enters the query cache already enriched and every
 * consumer reads one shape.
 */
/** Server "" defaults (and the goals []-when-empty quirk) read as unset. */
const orNull = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

export function enrichCharacter(projectId: number, character: ICharacter): ICharacter {
    const extras = character.id > 0 ? readExtras(projectId, character.id) : {};
    // Raw `categories` from the wire holds server LABELS; normalize before it
    // reaches any consumer. The detail endpoint omits the landed columns
    // entirely — every server read below degrades to the extras/legacy value.
    const serverCategories = fromServerCategories(character.categories);
    return {
        ...character,
        importance: extras.importance ?? null,
        categories: extras.categories ?? (serverCategories.length > 0 ? serverCategories : character.category ? [character.category] : []),
        characteristic: extras.characteristic ?? null,
        social_profession: extras.social_profession ?? orNull(character.profession),
        social_income: extras.social_income ?? orNull(character.household),
        social_education: extras.social_education ?? orNull(character.education),
        social_family: extras.social_family ?? orNull(character.family_status),
        personality: extras.personality ?? orNull(character.personality),
        problems: extras.problems ?? orNull(character.conflicts),
        motivation: extras.motivation ?? orNull(character.motivation),
        dreamcast_asset_ids: extras.dreamcast_asset_ids ?? [],
        reference_asset_ids: extras.reference_asset_ids ?? [],
        costume_asset_ids: extras.costume_asset_ids ?? [],
        asset_comments: extras.asset_comments ?? {},
        video_links: extras.video_links ?? [],
        actors: extras.actors ?? [],
    };
}

/**
 * Partitions a form patch into the real-API half and the mocked half.
 * After the swap EXTRA_FIELD_NAMES is empty and everything lands in `real`.
 */
export function splitCharacterPatch<T extends Record<string, unknown>>(patch: T): { real: Partial<T>; extras: Partial<CharacterExtras> } {
    const real: Record<string, unknown> = {};
    const extras: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(patch)) {
        if (EXTRA_FIELD_NAMES.has(key)) {
            extras[key] = value;
            // Landed column: mirror the non-empty value into the real update
            // under its wire name ('' would 422 — those edits stay mock-only).
            const serverField = SERVER_FIELD_BY_CONTRACT[key];
            if (serverField && isRealWritable(value)) real[serverField] = value;
            if (key === 'categories' && Array.isArray(value)) {
                const wire = toServerCategories(value as SignCharacter[]);
                // UPDATE drops categories today (missing array rule); sent
                // anyway so persistence begins the moment the backend fixes it.
                if (wire.length > 0) real.categories = wire;
            }
        } else {
            real[key] = value;
            const mirror = MIRROR_BY_LEGACY[key];
            if (mirror && isRealWritable(value)) real[mirror] = value;
        }
    }
    return { real: real as Partial<T>, extras: extras as Partial<CharacterExtras> };
}

/**
 * Splits a character's assets into the four photo collections. The single
 * place collection membership is decided: today the mocked id sets (pruned
 * against the actual assets), after the swap the asset's own `category`.
 * Actor photos belong to the casting actors and appear in no collection.
 */
export function splitPhotoCollections(
    character: Pick<ICharacter, 'assets' | 'dreamcast_asset_ids' | 'reference_asset_ids' | 'costume_asset_ids' | 'actors'>,
) {
    const dreamcastIds = new Set(character.dreamcast_asset_ids ?? []);
    const referenceIds = new Set(character.reference_asset_ids ?? []);
    const costumeIds = new Set(character.costume_asset_ids ?? []);
    const actorIds = new Set(actorPhotoAssetIds(character.actors));
    const photos: Assets[] = [];
    const dreamcast: Assets[] = [];
    const reference: Assets[] = [];
    const costumes: Assets[] = [];
    for (const asset of character.assets) {
        if (actorIds.has(asset.id)) continue;
        if (asset.category === assetCategory.dreamcast || dreamcastIds.has(asset.id)) {
            dreamcast.push(asset);
        } else if (asset.category === assetCategory.reference || referenceIds.has(asset.id)) {
            reference.push(asset);
        } else if (asset.category === assetCategory.costume || costumeIds.has(asset.id)) {
            costumes.push(asset);
        } else if (asset.category === assetCategory.image) {
            photos.push(asset);
        }
    }
    return { photos, dreamcast, reference, costumes };
}

/** Asset ids claimed by casting actors — kept out of both photo collections. */
export function actorPhotoAssetIds(actors: readonly CharacterActor[] | undefined): number[] {
    return (actors ?? []).map(actor => actor.photo_asset_id).filter((id): id is number => id !== null);
}
